/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Config, TypeEvent, TypeNiche, UsageChart } from '@/types';
import { useNavigate } from 'react-router-dom';
import {
  serviceListTypeNiche,
  serviceListTypeEvent,
  serviceUpdateTypeEvent,
} from '@/services/utils';
import { serviceUsageChart } from '@/services/usage';
import { serviceListConfigs } from '@/services/configs';
import AuthSession from '@/services/auth-session';

interface AppContextType {
  listTypeNiche: TypeNiche[];
  listTypeEvent: TypeEvent[];
  updateListTypeEvent: (eventId: number) => void;
  callApis: () => Promise<void>;
  usageChart: UsageChart[];
  configs: Config[];
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [listTypeNiche, setListTypeNiche] = useState<TypeNiche[]>([]);
  const [listTypeEvent, setListTypeEvent] = useState<TypeEvent[]>([]);
  const [usageChart, setUsageChart] = useState<UsageChart[]>([]);
  const [configs, setConfigs] = useState<Config[]>([]);
  const navigate = useNavigate();

  const fetchNiceTypes = async () => {
    try {
      const response = await serviceListTypeNiche();
      setListTypeNiche(response);
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') {
        console.log('>>>>err', err);
      }
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await serviceListConfigs();
      setConfigs(response);
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') {
        console.log('>>>>err', err);
      }
    }
  };

  const fetchTypeEvents = async () => {
    try {
      const response = await serviceListTypeEvent();
      setListTypeEvent(response);
    } catch (err: any) {
      const expired = err.message.includes('Sessão expirada');
      if (expired) {
        navigate('/');
      } else if (process.env.NODE_ENV === 'development') {
        console.log('>>>>err', err);
      }
    }
  };

  const fetchUsageChart = async () => {
    try {
      const response = await serviceUsageChart();
      setUsageChart(response);
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') {
        console.log('>>>>err', err);
      }
    }
  };

  const callApis = async () => {
    const { user } = AuthSession.currentSession();
    if (!user) return;
    await fetchNiceTypes();

    if (!user.isAdmin) return;
    await Promise.all([fetchTypeEvents(), fetchUsageChart(), fetchConfigs()]);
  };

  useEffect(() => {
    callApis();
  }, []);

  const updateListTypeEvent = (eventId: number) => {
    setListTypeEvent((prev) =>
      prev.map((v) => (v.id === eventId ? { ...v, active: !v.active } : v)),
    );
    serviceUpdateTypeEvent({ id: eventId }).catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('>>>>err', err);
      }
    });
  };

  return (
    <AppContext.Provider
      value={{ listTypeNiche, listTypeEvent, updateListTypeEvent, callApis, usageChart, configs }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
